import React, { useRef, useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { syncHistoryWithStore } from 'react-router-redux';
import { Router, hashHistory } from 'react-router';
import { locale, language } from '@tcc/i18n';
import { attachToGlobal } from '@tcc/treadstone'
import { getIdsFromHash } from './utils/url';
import { reqAppInitializer } from './actions/appInitializerActions';
import { MULTI_OPERATION_SELECTION } from './constants/featureFlags';
import { setupAllSteps } from './boot/config';
import routes from './routes';
import configureStore from './store';
import { isPluginMode } from './utils/isPluginMode';
import { initializeAnalytics } from './utils/analytics';
import { experimentalRedirect } from './boot/libs';
import { receiveOperations, receiveMultipleOperations } from './actions/operationsActions';
import { shouldUseMocks } from './utils/mocks';
import './global.css';

function DmbPlugin({ operations, operationsSelected }) {
  const [bootstrapped, setBootstrapped] = useState(false);
  const store = useRef();
  const [isProxying, setIsProxying] = useState(false);

  useEffect(() => {
    attachToGlobal();
    setupAllSteps().then(({ nav, env, auth }) => {
      const { currentUser, user } = auth;
      
      setIsProxying(user.isProxying);
      initializeAnalytics(currentUser.clientId);
      experimentalRedirect({ name: env.name, href: env.href });
      
      const storeConfig = {
        currentUser,
        user,
        app: {
          locale,
          language,
          ...env,
          nav: {
            ...nav,
          },
        },
      };
      store.current = configureStore(isPluginMode()
        ? storeConfig
        : {...storeConfig, hashHistory }
      );

      const { fieldId, mapLayerIds, seasonIds } = getIdsFromHash();

      store.current.dispatch(
        reqAppInitializer(fieldId, mapLayerIds, seasonIds)
      );
      setBootstrapped(true);
    });
  }, []);

  if (!bootstrapped) return null;

  const history = isPluginMode()
    ? hashHistory
    : syncHistoryWithStore(hashHistory, store.current);

  if (!isProxying && operations?.length && !shouldUseMocks()) {
    const state = store.current.getState();
    if (state.feature.userConfigFeatures[MULTI_OPERATION_SELECTION]?.value) {
      store.current.dispatch(receiveMultipleOperations(operationsSelected, operations))
    } else {
      store.current.dispatch(receiveOperations(operationsSelected[0], operations))
    }
  }
  
  return (
    <Provider store={store.current}>
      <Router
        history={history}
        routes={routes(isPluginMode() ? '/dmb' : '/')}
      />
    </Provider>
  );
}

export default DmbPlugin;
